import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTenantStore } from "../../stores/tenant";
import { api } from "../../lib/api";

type Status = "idle" | "connecting" | "ready" | "listening" | "thinking" | "speaking" | "ended" | "error";
type TtsMode = "elevenlabs" | "browser" | "off";

interface TranscriptTurn {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
}

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult:
    | ((ev: {
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
        resultIndex: number;
      }) => void)
    | null;
  onerror: ((ev: { error: string }) => void) | null;
  onend: (() => void) | null;
};

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:3001";
const WS_BASE = (() => {
  const fromEnv = import.meta.env.VITE_WS_URL as string | undefined;
  if (fromEnv) return fromEnv;
  return API_BASE.replace(/^http/, "ws");
})();

function makeSimSid(): string {
  return `sim-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function VoiceSimulator() {
  const available = useTenantStore((s) => s.available);
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const setTenant = useTenantStore((s) => s.setTenant);

  const tenantId = currentTenantId ?? available[0]?.id ?? "";
  const tenant = useMemo(() => available.find((t) => t.id === tenantId) ?? null, [available, tenantId]);

  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [turns, setTurns] = useState<TranscriptTurn[]>([]);
  const [interim, setInterim] = useState<string>("");
  const [ttsMode, setTtsMode] = useState<TtsMode>("elevenlabs");
  const [continuous, setContinuous] = useState(true);
  const [manualText, setManualText] = useState("");
  // Caller ID simulé. Vide = on n'envoie pas de numéro -> l'agent le DEMANDE
  // (évite qu'il propose un faux numéro de test).
  const [callerNumber, setCallerNumber] = useState("");
  const [elevenLabsStatus, setElevenLabsStatus] = useState<{
    configured: boolean;
    defaultVoiceId: string | null;
  }>({ configured: false, defaultVoiceId: null });

  const wsRef = useRef<WebSocket | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // File de synthèse vocale : on parle CHAQUE phrase dès qu'elle arrive (le
  // serveur envoie déjà des chunks par phrase) au lieu d'attendre toute la
  // réponse — l'agent commence à parler pendant qu'il génère la suite.
  const ttsQueueRef = useRef<string[]>([]);
  const ttsActiveRef = useRef<boolean>(false);
  const streamDoneRef = useRef<boolean>(false);
  // Incrémenté à chaque nouveau tour -> invalide une lecture/pré-génération en
  // cours (barge-in : un nouveau message coupe l'ancien).
  const ttsGenRef = useRef<number>(0);
  const currentAssistantIdRef = useRef<string | null>(null);
  // Erreurs « network » consécutives du service STT du navigateur (transitoires
  // -> reconnexion silencieuse ; alerte seulement si ça persiste).
  const sttNetworkErrorsRef = useRef<number>(0);
  // Après une interruption (« Interrompre »), on jette les tokens restants du
  // tour en cours (le serveur finit de streamer) jusqu'au marqueur `last`.
  const droppingTurnRef = useRef<boolean>(false);
  // Dernier texte interim non finalisé. Le service de reco de Chrome coupe
  // parfois en pleine phrase (erreur « network ») : le final n'arrive jamais et
  // l'interim resterait bloqué à l'écran. À la fin de session (onend), s'il
  // reste un interim orphelin, on l'ENVOIE comme message au lieu de le perdre.
  const pendingInterimRef = useRef<string>("");
  // Minuterie de clôture par STABILITÉ : Chrome attend un long silence pour
  // finaliser (et le bruit de fond repousse la clôture indéfiniment), alors que
  // le téléphone clôt après 600 ms. Si l'interim ne change plus pendant 1,3 s,
  // on l'envoie — latence proche du vrai appel, insensible au bruit ambiant.
  const interimTimerRef = useRef<number | null>(null);
  // Anti-doublon : quand la clôture par stabilité envoie le message, Chrome peut
  // ENSUITE émettre son propre résultat final avec le même texte -> le même tour
  // partirait deux fois (réponses dupliquées/concaténées). On mémorise le dernier
  // envoi pour ignorer un texte identique arrivant dans la foulée.
  const lastSentRef = useRef<{ text: string; at: number }>({ text: "", at: 0 });
  // DEMI-DUPLEX STRICT : micro réellement COUPÉ pendant que l'agent parle
  // (aucune contamination possible du flux de reco), puis réouverture après un
  // court délai de grâce — le temps que la queue audio des haut-parleurs meure,
  // sinon la fin de la question de l'agent (« …ou volontaire ? ») est transcrite
  // comme début de réponse de l'appelant (« vol »).
  const speakingEndedAtRef = useRef<number>(0);
  const agentTextRef = useRef<string>("");
  const recognitionAvailable = useMemo(() => getSpeechRecognitionCtor() !== null, []);
  const statusRef = useRef<Status>("idle");
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const language = tenant?.language ?? "fr-CA";
  const sttLang = language.startsWith("fr") ? "fr-CA" : language;

  useEffect(() => {
    api<{ configured: boolean; defaultVoiceId: string | null }>("/api/elevenlabs/status")
      .then((s) => {
        setElevenLabsStatus(s);
        if (!s.configured) setTtsMode((m) => (m === "elevenlabs" ? "browser" : m));
      })
      .catch(() => {});
  }, []);

  const appendTurn = useCallback((turn: TranscriptTurn) => {
    setTurns((prev) => [...prev, turn]);
  }, []);

  const appendToAssistant = useCallback((token: string) => {
    const id = currentAssistantIdRef.current;
    if (!id) return;
    // Texte du tour agent accumulé pour la garde anti-écho (voir onresult).
    agentTextRef.current += token;
    setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, text: t.text + token } : t)));
  }, []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.src = "";
      } catch {}
      audioRef.current = null;
    }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  // Génère l'audio (sans le jouer) -> permet de pré-générer la phrase suivante
  // pendant que la précédente joue.
  const fetchElevenLabsUrl = useCallback(
    async (text: string): Promise<string | null> => {
      try {
        const res = await fetch(`${API_BASE}/api/elevenlabs/tts`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text,
            voiceId: tenant?.voiceId ?? undefined,
            // Reproduit le modèle/les réglages voix du tenant (comme au téléphone).
            voiceSettings: tenant?.voiceSettings ?? undefined,
            // Force la prononciation dans la langue du tenant (fr/en) — sans ça,
            // ElevenLabs auto-détecte et anglicise les phrases courtes.
            language: tenant?.language ?? undefined,
          }),
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    },
    [tenant],
  );

  const playUrl = useCallback((url: string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const audio = new Audio(url);
      audioRef.current = audio;
      const cleanup = () => {
        URL.revokeObjectURL(url);
        if (audioRef.current === audio) audioRef.current = null;
        resolve();
      };
      audio.onended = cleanup;
      audio.onerror = cleanup;
      audio.play().catch(cleanup);
    });
  }, []);

  const playElevenLabs = useCallback(
    async (text: string): Promise<void> => {
      const url = await fetchElevenLabsUrl(text);
      if (url) await playUrl(url);
      else throw new Error("tts failed");
    },
    [fetchElevenLabsUrl, playUrl],
  );

  const playBrowser = useCallback(
    (text: string): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (!("speechSynthesis" in window)) {
          resolve();
          return;
        }
        const u = new SpeechSynthesisUtterance(text);
        u.lang = sttLang;
        u.rate = 1;
        u.onend = () => resolve();
        u.onerror = () => resolve();
        window.speechSynthesis.speak(u);
      });
    },
    [sttLang],
  );

  const speak = useCallback(
    async (text: string): Promise<void> => {
      if (!text || ttsMode === "off") return;
      if (ttsMode === "elevenlabs" && elevenLabsStatus.configured) {
        try {
          await playElevenLabs(text);
          return;
        } catch (err) {
          console.warn("[vsim] ElevenLabs TTS failed, fallback browser:", err);
        }
      }
      await playBrowser(text);
    },
    [elevenLabsStatus.configured, playBrowser, playElevenLabs, ttsMode],
  );

  const stopRecognition = useCallback(() => {
    // Arrêt volontaire (l'agent va parler, raccrochage…) : on jette aussi tout
    // interim en attente pour qu'il ne parte pas par erreur plus tard.
    pendingInterimRef.current = "";
    if (interimTimerRef.current) {
      window.clearTimeout(interimTimerRef.current);
      interimTimerRef.current = null;
    }
    const rec = recRef.current;
    if (!rec) return;
    try {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      rec.abort();
    } catch {}
    recRef.current = null;
  }, []);

  // Interruption : coupe NET la parole de l'agent (audio en cours + file de
  // phrases + reste du tour en streaming) sans envoyer de message.
  const cancelSpeech = useCallback(() => {
    ttsGenRef.current += 1;
    ttsQueueRef.current = [];
    // Si le modèle streame encore ce tour, on jettera ses tokens jusqu'à `last`.
    if (!streamDoneRef.current) droppingTurnRef.current = true;
    stopAudio();
    // Fin (forcée) de la parole agent : armer le filet anti-écho.
    speakingEndedAtRef.current = Date.now();
    if (statusRef.current === "speaking") {
      setStatus("listening");
    }
  }, [stopAudio]);

  const sendPrompt = useCallback(
    (text: string) => {
      const ws = wsRef.current;
      const trimmed = text.trim();
      if (!ws || ws.readyState !== ws.OPEN || !trimmed) return;
      // Anti-doublon (fantôme de Chrome après la clôture par stabilité) :
      // - texte identique pendant que l'agent traite/parle -> TOUJOURS ignoré ;
      // - texte identique en écoute -> ignoré sous 4 s (Chrome peut finaliser tard).
      const now = Date.now();
      if (trimmed === lastSentRef.current.text) {
        const inFlight = statusRef.current === "thinking" || statusRef.current === "speaking";
        if (inFlight || now - lastSentRef.current.at < 4000) return;
      }
      lastSentRef.current = { text: trimmed, at: now };
      stopAudio();
      appendTurn({ id: `u-${Date.now()}`, role: "user", text: trimmed });
      const aid = `a-${Date.now()}`;
      currentAssistantIdRef.current = aid;
      appendTurn({ id: aid, role: "assistant", text: "" });
      agentTextRef.current = "";
      ttsQueueRef.current = [];
      streamDoneRef.current = false;
      droppingTurnRef.current = false;
      ttsGenRef.current += 1;
      setStatus("thinking");
      ws.send(JSON.stringify({ type: "prompt", voicePrompt: trimmed, last: true }));
    },
    [appendTurn, stopAudio],
  );

  const startRecognition = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return;
    stopRecognition();
    const rec = new Ctor();
    rec.lang = sttLang;
    rec.continuous = continuous;
    rec.interimResults = true;

    rec.onresult = (ev) => {
      let finalText = "";
      let interimText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i] as ArrayLike<{ transcript: string }> & { isFinal: boolean };
        const chunk = (r[0] as unknown as { transcript: string }).transcript;
        if (r.isFinal) finalText += chunk;
        else interimText += chunk;
      }
      // La reconnaissance fonctionne -> on repart de zéro côté erreurs réseau.
      sttNetworkErrorsRef.current = 0;
      const norm = (s: string) =>
        s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

      // FILET ANTI-ÉCHO : le micro est coupé pendant la parole de l'agent (demi-
      // duplex) et rouvert après un délai de grâce, mais si un résidu de sa voix
      // passe quand même, un résultat contenu dans ce qu'il vient de dire
      // (« convient ouais » -> « convient » ; relecture complète répétée) est
      // nettoyé : les mots de tête appartenant à la phrase agent sont retirés,
      // un résultat entièrement contenu est jeté.
      const sinceSpeech = Date.now() - speakingEndedAtRef.current;
      const agentNorm = norm(agentTextRef.current);
      const cleanEcho = (raw: string): string | null => {
        if (sinceSpeech >= 3000 || !agentNorm) return raw;
        const n = norm(raw);
        if (n.length >= 3 && agentNorm.includes(n)) return null; // écho pur
        // Retire les mots de TÊTE qui prolongent la phrase de l'agent
        // (queue audio des haut-parleurs), ex « convient ouais c'est pas mal ».
        const words = raw.trim().split(/\s+/);
        let skip = 0;
        while (skip < words.length && skip < 6) {
          const head = norm(words.slice(0, skip + 1).join(" "));
          if (head && agentNorm.includes(head)) skip++;
          else break;
        }
        const rest = words.slice(skip).join(" ");
        return rest.trim() ? rest : null;
      };

      if (interimText) {
        const cleaned = cleanEcho(interimText);
        if (cleaned === null) return; // encore de l'écho, on attend l'appelant
        setInterim(cleaned);
        pendingInterimRef.current = cleaned;
        // Clôture par stabilité : texte inchangé pendant 1,3 s -> on envoie.
        if (interimTimerRef.current) window.clearTimeout(interimTimerRef.current);
        interimTimerRef.current = window.setTimeout(() => {
          interimTimerRef.current = null;
          const t = pendingInterimRef.current.trim();
          if (t && statusRef.current === "listening") {
            pendingInterimRef.current = "";
            setInterim("");
            sendPrompt(t);
            // On jette la session de reco : sinon Chrome finaliserait ENSUITE le
            // même texte -> double envoi. L'abort relance une session neuve.
            try {
              recRef.current?.abort();
            } catch {}
          }
        }, 1300);
      }
      if (finalText) {
        if (interimTimerRef.current) {
          window.clearTimeout(interimTimerRef.current);
          interimTimerRef.current = null;
        }
        setInterim("");
        pendingInterimRef.current = "";
        const cleaned = cleanEcho(finalText);
        if (cleaned === null || !cleaned.trim()) return; // écho pur
        sendPrompt(cleaned);
      }
    };

    rec.onerror = (ev) => {
      if (ev.error === "no-speech" || ev.error === "aborted") return;
      // « network » : le service de reconnaissance de Chrome décroche parfois en
      // pleine session (surtout avec le micro ouvert en continu). Transitoire :
      // rec.onend relance automatiquement. On n'affiche une alerte qu'à partir
      // de 3 échecs consécutifs (vrai problème réseau/navigateur).
      if (ev.error === "network") {
        sttNetworkErrorsRef.current += 1;
        if (sttNetworkErrorsRef.current < 3) return;
        setErrorMsg(
          "La dictée du navigateur ne répond pas. Elle n'est fiable que sur Google Chrome (Arc, Brave et Safari échouent en « network »). Utilise Chrome, écris dans la zone de texte, ou appelle le numéro du tenant pour le vrai rendu téléphonique.",
        );
        return;
      }
      setErrorMsg(`STT: ${ev.error}`);
    };

    rec.onend = () => {
      // Session coupée avec un interim jamais finalisé (coupure « network » de
      // Chrome en pleine phrase) : on récupère ce que la personne a dit et on
      // l'envoie, au lieu de laisser le message bloqué en italique à l'écran.
      const orphan = pendingInterimRef.current.trim();
      if (orphan && statusRef.current === "listening") {
        pendingInterimRef.current = "";
        setInterim("");
        sendPrompt(orphan);
      }
      if (
        wsRef.current &&
        wsRef.current.readyState === wsRef.current.OPEN &&
        continuous &&
        statusRef.current !== "ended" &&
        statusRef.current !== "error"
      ) {
        try {
          rec.start();
        } catch {}
      }
    };

    recRef.current = rec;
    try {
      rec.start();
    } catch (err) {
      setErrorMsg(`STT start: ${(err as Error).message}`);
    }
  }, [cancelSpeech, continuous, sendPrompt, sttLang, stopRecognition]);

  const hangup = useCallback(() => {
    stopRecognition();
    stopAudio();
    const ws = wsRef.current;
    if (ws && ws.readyState === ws.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "end", reason: "user_hangup" }));
      } catch {}
      ws.close();
    }
    wsRef.current = null;
    setStatus("ended");
    setInterim("");
  }, [stopAudio, stopRecognition]);

  // Vide la file en parlant phrase par phrase. Reprend l'écoute une fois la
  // file vide ET le flux terminé. Idempotente (un seul drain à la fois).
  const pumpTts = useCallback(async () => {
    if (ttsActiveRef.current) return;
    ttsActiveRef.current = true;

    const gen = ttsGenRef.current;
    const useEleven = ttsMode === "elevenlabs" && elevenLabsStatus.configured;
    const stale = () =>
      ttsGenRef.current !== gen || statusRef.current === "ended" || statusRef.current === "error";

    if (ttsQueueRef.current.length > 0 && ttsMode !== "off") {
      // DEMI-DUPLEX STRICT : micro réellement coupé pendant que l'agent parle.
      // Une session de reco laissée ouverte transcrit la voix de l'agent (haut-
      // parleurs) et colle sa phrase devant celle de l'appelant — irrécupérable.
      stopRecognition();
      setInterim("");
      setStatus("speaking");
    }

    // On pré-génère la phrase SUIVANTE pendant qu'on joue la courante.
    let prefetch: { text: string; url: Promise<string | null> } | null = null;

    while (ttsQueueRef.current.length > 0 || prefetch) {
      let text: string;
      let urlPromise: Promise<string | null> | null = null;
      if (prefetch) {
        ({ text } = prefetch);
        urlPromise = prefetch.url;
        prefetch = null;
      } else {
        text = ttsQueueRef.current.shift()!;
        if (useEleven) urlPromise = fetchElevenLabsUrl(text);
      }

      // Démarre la pré-génération de la suivante (ElevenLabs uniquement).
      if (useEleven && ttsQueueRef.current.length > 0) {
        const next = ttsQueueRef.current.shift()!;
        prefetch = { text: next, url: fetchElevenLabsUrl(next) };
      }

      try {
        if (useEleven) {
          const url = urlPromise ? await urlPromise : null;
          if (stale()) {
            if (url) URL.revokeObjectURL(url);
          } else if (url) {
            await playUrl(url);
          } else {
            await playBrowser(text);
          }
        } else {
          await speak(text);
        }
      } catch {
        /* ignore une phrase qui échoue */
      }

      if (stale()) {
        ttsActiveRef.current = false;
        // Tour invalidé (barge-in) -> on relance pour le tour courant si besoin.
        if (ttsGenRef.current !== gen) void pumpTts();
        return;
      }
    }

    ttsActiveRef.current = false;

    if (ttsQueueRef.current.length > 0) {
      void pumpTts();
      return;
    }

    if (streamDoneRef.current && statusRef.current !== "ended" && statusRef.current !== "error") {
      currentAssistantIdRef.current = null;
      speakingEndedAtRef.current = Date.now();
      if (recognitionAvailable && continuous) {
        setStatus("listening");
        // DÉLAI DE GRÂCE (350 ms) avant de rouvrir le micro : la queue audio des
        // haut-parleurs meurt, sinon la fin de la question de l'agent
        // (« …ou volontaire ? ») est transcrite comme réponse (« vol »).
        window.setTimeout(() => {
          if (
            statusRef.current === "listening" &&
            !recRef.current &&
            wsRef.current &&
            wsRef.current.readyState === wsRef.current.OPEN
          ) {
            startRecognition();
          }
        }, 350);
      } else {
        setStatus("ready");
      }
    }
  }, [
    continuous,
    elevenLabsStatus.configured,
    fetchElevenLabsUrl,
    playBrowser,
    playUrl,
    recognitionAvailable,
    speak,
    startRecognition,
    stopRecognition,
    ttsMode,
  ]);

  const startCall = useCallback(async () => {
    if (!tenantId) {
      setErrorMsg("Aucun tenant sélectionné.");
      return;
    }
    setErrorMsg(null);
    setTurns([]);
    setInterim("");
    setStatus("connecting");

    if (recognitionAvailable) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) {
        setErrorMsg(`Mic refusé: ${(err as Error).message}`);
        setStatus("error");
        return;
      }
    }

    const callSid = makeSimSid();
    const from = callerNumber.trim();
    const url =
      `${WS_BASE}/voice/relay?tenantId=${encodeURIComponent(tenantId)}&callSid=${encodeURIComponent(callSid)}` +
      (from ? `&from=${encodeURIComponent(from)}` : "");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(
        JSON.stringify({
          type: "setup",
          callSid,
          from: from || undefined,
          to: "+15550100199",
        }),
      );
      setStatus("ready");
      const greeting = tenant?.greetingMessage;
      if (greeting) {
        const aid = `a-${Date.now()}`;
        appendTurn({ id: aid, role: "assistant", text: greeting });
        setStatus("speaking");
        void speak(greeting).then(() => {
          if (statusRef.current === "ended" || statusRef.current === "error") return;
          if (recognitionAvailable && continuous) {
            setStatus("listening");
            startRecognition();
          } else {
            setStatus("ready");
          }
        });
      } else if (recognitionAvailable && continuous) {
        setStatus("listening");
        startRecognition();
      }
    };

    ws.onmessage = (ev) => {
      let msg: { type?: string; token?: string; last?: boolean; handoffData?: string };
      try {
        msg = JSON.parse(typeof ev.data === "string" ? ev.data : "");
      } catch {
        return;
      }
      if (msg.type === "text") {
        // Tour interrompu par l'utilisateur : on jette le reste du stream
        // (transcript inclus) jusqu'au marqueur de fin de tour.
        if (droppingTurnRef.current) {
          if (msg.last) {
            droppingTurnRef.current = false;
            streamDoneRef.current = true;
          }
          return;
        }
        const tk = msg.token ?? "";
        if (tk) {
          appendToAssistant(tk);
          if (tk.trim()) ttsQueueRef.current.push(tk);
          void pumpTts();
        }
        if (msg.last) {
          streamDoneRef.current = true;
          void pumpTts();
        }
      } else if (msg.type === "end") {
        appendTurn({
          id: `s-${Date.now()}`,
          role: "system",
          text: msg.handoffData ? `Transfert demandé: ${msg.handoffData}` : "Appel terminé par l'agent.",
        });
        hangup();
      }
    };

    ws.onerror = () => {
      setErrorMsg("WebSocket error");
      setStatus("error");
    };

    ws.onclose = () => {
      stopRecognition();
      if (statusRef.current !== "ended" && statusRef.current !== "error") setStatus("ended");
    };
  }, [
    appendToAssistant,
    appendTurn,
    continuous,
    pumpTts,
    hangup,
    recognitionAvailable,
    speak,
    startRecognition,
    stopRecognition,
    tenant,
    tenantId,
  ]);

  useEffect(() => {
    return () => {
      stopRecognition();
      stopAudio();
      const ws = wsRef.current;
      if (ws && ws.readyState === ws.OPEN) ws.close();
    };
  }, [stopAudio, stopRecognition]);

  const onSubmitManual = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualText.trim()) return;
    sendPrompt(manualText);
    setManualText("");
  };

  const callActive = status !== "idle" && status !== "ended" && status !== "error";
  const pushToTalk = callActive && recognitionAvailable && !continuous;

  return (
    <div className="vsim">
      <header className="vsim__header">
        <div>
          <h1 className="vsim__title">Simulateur vocal</h1>
          <p className="vsim__subtitle">
            Teste l'agent en conditions réelles, directement dans le navigateur — voix, écoute et
            raisonnement, sans passer d'appel.
          </p>
        </div>
      </header>

      <div className="vsim__grid">
        {/* ---------- Panneau d'appel ---------- */}
        <section className="vsim__call">
          <div className="vsim__agent">
            <div className={`vsim__orb vsim__orb--${status}`}>
              <span className="vsim__orb-ring" />
              <span className="vsim__orb-ring" />
              <span className="vsim__orb-core">
                <PhoneGlyph status={status} />
              </span>
            </div>
            <div className="vsim__agent-name">{tenant?.name ?? "Aucun établissement"}</div>
            <div className={`vsim__status vsim__status--${status}`}>
              <span className="vsim__status-dot" />
              {statusLabel(status)}
            </div>
          </div>

          <div className="vsim__setup">
            <label className="vsim__field">
              <span className="vsim__field-label">Établissement</span>
              <select
                className="vsim__select"
                value={tenantId}
                onChange={(e) => setTenant(e.target.value)}
                disabled={callActive}
              >
                {available.length === 0 ? <option value="">— aucun —</option> : null}
                {available.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.type})
                  </option>
                ))}
              </select>
            </label>

            <label className="vsim__field">
              <span className="vsim__field-label">Voix</span>
              <select
                className="vsim__select"
                value={ttsMode}
                onChange={(e) => setTtsMode(e.target.value as TtsMode)}
              >
                <option value="elevenlabs" disabled={!elevenLabsStatus.configured}>
                  ElevenLabs{elevenLabsStatus.configured ? "" : " (non configuré)"}
                </option>
                <option value="browser">Navigateur</option>
                <option value="off">Désactivée</option>
              </select>
            </label>

            <label className="vsim__field">
              <span className="vsim__field-label">Numéro appelant</span>
              <input
                className="vsim__input"
                type="tel"
                value={callerNumber}
                onChange={(e) => setCallerNumber(e.target.value)}
                placeholder="vide = l'agent le demandera"
                disabled={callActive}
              />
            </label>

            <button
              type="button"
              className={`vsim__toggle ${continuous ? "is-on" : ""}`}
              onClick={() => !callActive && setContinuous((v) => !v)}
              disabled={callActive}
              role="switch"
              aria-checked={continuous}
            >
              <span className="vsim__toggle-track">
                <span className="vsim__toggle-thumb" />
              </span>
              <span className="vsim__toggle-label">
                {continuous ? "Écoute continue" : "Push-to-talk"}
              </span>
            </button>
          </div>

          <div className="vsim__cta">
            {!callActive ? (
              <button
                className="vsim__call-btn vsim__call-btn--start"
                onClick={startCall}
                disabled={!tenantId}
              >
                <PhoneGlyph status="ready" />
                Démarrer l'appel
              </button>
            ) : (
              <button className="vsim__call-btn vsim__call-btn--end" onClick={hangup}>
                <HangupGlyph />
                Raccrocher
              </button>
            )}
            {callActive && status === "speaking" ? (
              <button
                className="vsim__ptt"
                onClick={() => {
                  cancelSpeech();
                  if (recognitionAvailable && continuous && !recRef.current) startRecognition();
                }}
              >
                ✋ Interrompre
              </button>
            ) : null}
            {pushToTalk ? (
              <button
                className="vsim__ptt"
                onMouseDown={() => startRecognition()}
                onMouseUp={() => stopRecognition()}
                onTouchStart={() => startRecognition()}
                onTouchEnd={() => stopRecognition()}
              >
                <span className="vsim__ptt-dot" />
                Maintenir pour parler
              </button>
            ) : null}
          </div>

          {ttsMode === "elevenlabs" && !elevenLabsStatus.configured ? (
            <p className="vsim__note vsim__note--warn">
              ElevenLabs n'est pas configuré. Renseigne <code>ELEVENLABS_API_KEY</code> et{" "}
              <code>ELEVENLABS_DEFAULT_VOICE_ID</code>, puis relance l'API.
            </p>
          ) : null}
          {!recognitionAvailable ? (
            <p className="vsim__note vsim__note--warn">
              Reconnaissance vocale indisponible ici (utilise Chrome). Tu peux tester via la zone de
              texte.
            </p>
          ) : null}
          {errorMsg ? <p className="vsim__note vsim__note--error">{errorMsg}</p> : null}
        </section>

        {/* ---------- Conversation ---------- */}
        <section className="vsim__conversation">
          <div className="vsim__messages">
            {turns.length === 0 && !interim ? (
              <div className="vsim__empty">
                <div className="vsim__empty-icon">
                  <PhoneGlyph status="idle" />
                </div>
                <p className="vsim__empty-title">Aucune conversation</p>
                <p className="vsim__empty-sub">
                  Démarre l'appel et parle, ou écris un message ci-dessous.
                </p>
              </div>
            ) : (
              <ul className="vsim__bubbles">
                {turns.map((t) => (
                  <li key={t.id} className={`vsim__bubble vsim__bubble--${t.role}`}>
                    <span className="vsim__bubble-role">
                      {t.role === "user" ? "Vous" : t.role === "assistant" ? "Agent" : "Système"}
                    </span>
                    <span className="vsim__bubble-text">{t.text || "…"}</span>
                  </li>
                ))}
                {interim ? (
                  <li className="vsim__bubble vsim__bubble--user vsim__bubble--interim">
                    <span className="vsim__bubble-role">Vous</span>
                    <span className="vsim__bubble-text">{interim}</span>
                  </li>
                ) : null}
              </ul>
            )}
          </div>

          <form className="vsim__composer" onSubmit={onSubmitManual}>
            <input
              type="text"
              placeholder={callActive ? "Écris un message à l'agent…" : "Démarre l'appel pour écrire…"}
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              disabled={!callActive}
            />
            <button
              className="vsim__send"
              type="submit"
              disabled={!callActive || !manualText.trim()}
              aria-label="Envoyer"
            >
              <SendGlyph />
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

function PhoneGlyph({ status }: { status: Status }) {
  if (status === "listening" || status === "ready") {
    // Micro (à l'écoute)
    return (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10a7 7 0 0 0 14 0" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
    );
  }
  if (status === "speaking") {
    // Onde sonore (parle)
    return (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <line x1="4" y1="10" x2="4" y2="14" />
        <line x1="9" y1="6" x2="9" y2="18" />
        <line x1="14" y1="8" x2="14" y2="16" />
        <line x1="19" y1="11" x2="19" y2="13" />
      </svg>
    );
  }
  // Téléphone (par défaut)
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function HangupGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function SendGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function statusLabel(s: Status): string {
  switch (s) {
    case "idle":
      return "Inactif";
    case "connecting":
      return "Connexion…";
    case "ready":
      return "Connecté";
    case "listening":
      return "À l'écoute";
    case "thinking":
      return "Réflexion…";
    case "speaking":
      return "Parle";
    case "ended":
      return "Terminé";
    case "error":
      return "Erreur";
  }
}
